/**
 * Question rating (SPEC §2.5, DESIGN.md §3.6). A cream bar under the ranking on
 * the reveal surface: one question, two 44px buttons, on every phone.
 *
 * Anonymous, one vote per player per question, never surfaced in-game — and
 * never a gate. It sits above End turn and has no bearing on it: the turn ends
 * when the active player says so, whether or not anyone rated anything.
 */
import type { ThumbsVote } from '@idgame/protocol';

export interface ThumbsWidgetProps {
  /** The vote already on record, if any. */
  vote?: ThumbsVote | null;
  /** True once this client has voted on this question (`justification.selfVoted`). */
  voted?: boolean;
  onVote: (vote: ThumbsVote) => void;
}

export function ThumbsWidget({ vote = null, voted = false, onVote }: ThumbsWidgetProps) {
  const done = voted || vote !== null;

  return (
    <div
      data-testid="thumbs-widget"
      className="flex items-center justify-between gap-3 rounded-card border border-line bg-cream py-1.5 pr-1.5 pl-4"
    >
      {/* Not a heading: it is asking for a favour, not setting the screen's subject. */}
      <p className="text-[0.8125rem] font-semibold text-ink">Was this a good question?</p>
      <div className="flex shrink-0 gap-2">
        {(['up', 'down'] as const).map((kind) => {
          const active = vote === kind;
          return (
            <button
              key={kind}
              type="button"
              data-testid={`thumbs-${kind}`}
              aria-label={kind === 'up' ? 'Good question' : 'Bad question'}
              aria-pressed={active}
              disabled={done}
              onClick={() => onVote(kind)}
              className={`flex h-11 w-11 items-center justify-center rounded-btn border text-base transition ${
                active
                  ? 'border-terracotta bg-terracotta'
                  : 'border-line bg-cream disabled:border-transparent disabled:bg-disabled disabled:opacity-45'
              }`}
            >
              <span aria-hidden="true">{kind === 'up' ? '👍' : '👎'}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
